import { authorizedFetch } from '../utils/api';

/**
 * Liest den Body einer fetch-Response defensiv als JSON.
 *
 * Hintergrund: Liefert der Server einen NICHT-JSON-Body (leere Antwort,
 * HTML-Fehlerseite vom Gateway/Proxy, abgeschnittene Antwort), wirft
 * response.json() in Safari/WKWebView die kryptische
 * "SyntaxError: The string did not match the expected pattern". Diese landete
 * ohne HTTP-Kontext als eigenes, nicht verwertbares Sentry-Issue
 * (JAVASCRIPT-REACT-HH). Wir uebersetzen das in eine klare, status-tragende
 * Meldung, statt die Roh-SyntaxError durchzureichen.
 *
 * @param {Response} response - fetch-Response
 * @param {string} fallbackMessage - Klartext-Meldung, falls der Body kein JSON ist
 * @returns {Promise<Object>} geparstes JSON
 */
const parseJsonSafely = async (response, fallbackMessage) => {
  try {
    return await response.json();
  } catch (parseError) {
    const status = response?.status ?? 'unbekannt';
    const error = new Error(`${fallbackMessage} (HTTP ${status})`);
    error.cause = parseError;
    throw error;
  }
};

/**
 * LeaderboardService - Handles all Leaderboard/Aura API calls
 * Production-Ready with Error Handling + Sentry Integration
 */
const LeaderboardService = {
  /**
   * Fetch school-wide leaderboard
   * @param {number} limit - Number of users to fetch (default: 10, Top 10 der Schueler)
   * @returns {Promise<Object>} { leaderboard: Array, currentUserRank: number|null, meta: Object }
   */
  fetchLeaderboard: async (limit = 10) => {
    try {
      console.log('📊 LeaderboardService: Fetching leaderboard...', { limit });

      const response = await authorizedFetch(`/api/v1/aura/leaderboard?limit=${limit}`);

      if (!response.ok) {
        const error = await parseJsonSafely(response, 'Leaderboard konnte nicht geladen werden');
        throw new Error(error.message || error.error || 'Leaderboard konnte nicht geladen werden');
      }

      const data = await parseJsonSafely(response, 'Leaderboard konnte nicht geladen werden');

      console.log('✅ LeaderboardService: Leaderboard erfolgreich geladen', {
        users: data.leaderboard?.length || 0,
        currentUserRank: data.currentUserRank,
        hasLevelThresholds: !!data.levelThresholds,
      });

      return {
        // Aura v2 (Okt 2026): Die Schule kann die Rangliste abschalten (enabled: false),
        // jede Person kann sich selbst ausblenden (showOnLeaderboard: false).
        // Alte Server ohne die Felder: Rangliste an, Sichtbarkeit unbekannt.
        enabled: data.enabled !== false,
        showOnLeaderboard: typeof data.showOnLeaderboard === 'boolean' ? data.showOnLeaderboard : null,
        leaderboard: data.leaderboard || [],
        currentUserRank: data.currentUserRank || null,
        // ✅ Tenant-spezifische Level-Schwellenwerte für Fortschrittsberechnung
        levelThresholds: data.levelThresholds || { 1: 0, 2: 500, 3: 1000, 4: 2000, 5: 5000 },
        meta: data.meta || {},
      };
    } catch (error) {
      console.error('❌ LeaderboardService: Fehler beim Laden des Leaderboards', error);

      // Sentry Error Tracking (falls Sentry aktiv)
      if (window.Sentry) {
        window.Sentry.captureException(error, {
          tags: { service: 'LeaderboardService', method: 'fetchLeaderboard' },
          extra: { limit },
        });
      }

      throw error;
    }
  },

  /**
   * Eigene Sichtbarkeit in der Rangliste setzen (PATCH /aura/leaderboard-visibility).
   * Wirkt nur auf das eigene Konto, die Aura bleibt unveraendert.
   * @param {boolean} show
   * @returns {Promise<boolean>} gespeicherter Wert
   */
  setLeaderboardVisibility: async (show) => {
    const response = await authorizedFetch('/api/v1/aura/leaderboard-visibility', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ showOnLeaderboard: !!show }),
    });
    const data = await parseJsonSafely(response, 'Die Einstellung konnte nicht gespeichert werden');
    if (!response.ok || data.success === false) {
      throw new Error(data.error || 'Die Einstellung konnte nicht gespeichert werden');
    }
    const saved = data.data?.showOnLeaderboard;
    return typeof saved === 'boolean' ? saved : !!show;
  },

  /**
   * Get current user's rank (extracted from leaderboard data)
   * @param {Array} leaderboardData - Array of leaderboard users
   * @param {string} currentUserId - Current user's ID
   * @returns {number|null} Rank (1-based) or null if not found
   */
  getCurrentUserRank: (leaderboardData, currentUserId) => {
    if (!leaderboardData || !Array.isArray(leaderboardData) || !currentUserId) {
      return null;
    }

    const userIndex = leaderboardData.findIndex((user) => user.id === currentUserId);
    return userIndex >= 0 ? userIndex + 1 : null;
  },

  /**
   * Fetch Aura stats for current user
   * @returns {Promise<Object>} User's aura stats
   */
  fetchMyAuraStats: async () => {
    try {
      console.log('📈 LeaderboardService: Fetching user aura stats...');

      const response = await authorizedFetch('/api/v1/aura/stats');

      if (!response.ok) {
        const error = await parseJsonSafely(response, 'Aura-Statistiken konnten nicht geladen werden');
        throw new Error(error.message || error.error || 'Aura-Statistiken konnten nicht geladen werden');
      }

      const data = await parseJsonSafely(response, 'Aura-Statistiken konnten nicht geladen werden');

      console.log('✅ LeaderboardService: Aura-Stats erfolgreich geladen', data.stats);

      return data.stats || {};
    } catch (error) {
      console.error('❌ LeaderboardService: Fehler beim Laden der Aura-Stats', error);

      // Sentry Error Tracking
      if (window.Sentry) {
        window.Sentry.captureException(error, {
          tags: { service: 'LeaderboardService', method: 'fetchMyAuraStats' },
        });
      }

      throw error;
    }
  },

  /**
   * Aura-Regeln der eigenen Schule laden (Belohnungen, Limits, Level-Schwellen).
   * Die Aura-Status-Seite zeigt damit die Werte, die im Admin-Panel eingestellt sind,
   * statt hartcodierter Standardzahlen.
   * @returns {Promise<Object|null>} Config oder null, wenn sie nicht geladen werden konnte
   */
  fetchAuraConfig: async () => {
    try {
      const response = await authorizedFetch('/api/v1/aura/config');

      if (!response.ok) {
        const error = await parseJsonSafely(response, 'Aura-Konfiguration konnte nicht geladen werden');
        throw new Error(error.message || error.error || 'Aura-Konfiguration konnte nicht geladen werden');
      }

      const data = await parseJsonSafely(response, 'Aura-Konfiguration konnte nicht geladen werden');
      return data.data || null;
    } catch (error) {
      // Nicht kritisch: die Seite faellt auf die Standardwerte zurueck.
      console.error('❌ LeaderboardService: Fehler beim Laden der Aura-Config', error);
      return null;
    }
  },

  /**
   * Die letzten eigenen Aura-Buchungen (neueste zuerst) - fuer "Letzte Aktionen"
   * auf der Aura-Seite. Nicht kritisch: bei Fehlern bleibt die Liste leer.
   * @param {number} [limit=5]
   * @returns {Promise<Array>} Transaktionen ({ id, amount, type, metadata, createdAt })
   */
  fetchMyAuraHistory: async (limit = 5) => {
    try {
      const response = await authorizedFetch(`/api/v1/aura/history?limit=${encodeURIComponent(limit)}`);

      if (!response.ok) {
        const error = await parseJsonSafely(response, 'Aura-Verlauf konnte nicht geladen werden');
        throw new Error(error.message || error.error || 'Aura-Verlauf konnte nicht geladen werden');
      }

      const data = await parseJsonSafely(response, 'Aura-Verlauf konnte nicht geladen werden');
      return Array.isArray(data.transactions) ? data.transactions : [];
    } catch (error) {
      console.error('❌ LeaderboardService: Fehler beim Laden des Aura-Verlaufs', error);
      return [];
    }
  },
};

export default LeaderboardService;
